import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { IconArrowBack } from "@boligportal/bricks-icons";
import { Button } from "@boligportal/bricks-ui";
import { CheckoutProvider } from "@stripe/react-stripe-js/checkout";
import { loadStripe } from "@stripe/stripe-js";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useResetAtom } from "jotai/utils";
import { z } from "zod";
import { customerQueries } from "~/api/customer/queries";
import { TierLookupKeyEnum } from "~/api/generated/model";
import { InvoiceContactDialogButton } from "~/routes/(restricted)/checkout/-components/InvoiceContactDialog/InvoiceContactDialogButton";
import { OrderSummary } from "~/routes/(restricted)/checkout/-components/OrderSummary";
import { PaymentForm } from "~/routes/(restricted)/checkout/-components/PaymentForm";
import {
  submissionErrorAtom,
  submissionStatusAtom,
} from "~/routes/(restricted)/checkout/-state/submission-atoms";
import { PRICE_LOOKUP_KEYS } from "~/routes/(restricted)/checkout/-utils/keys";

export const checkoutSearchSchema = z.object({
  product: z.enum([
    TierLookupKeyEnum.Basic,
    TierLookupKeyEnum.Premium,
    TierLookupKeyEnum.Analyse,
  ]),
  from: z.string().optional(),
});

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY, {
  locale: "da",
});

export const Route = createFileRoute("/(restricted)/checkout/")({
  component: CheckoutPage,
  validateSearch: checkoutSearchSchema,
  loader: async ({ context: { i18n } }) => {
    await i18n.loadNamespaces("checkout");

    const title = i18n.t("checkout:meta.title");

    return { meta: { title } };
  },
  head: async (context) => {
    return {
      meta: [
        {
          title: context.loaderData?.meta.title,
        },
      ],
    };
  },
});

function CheckoutPage() {
  const { t } = useTranslation(["checkout"]);
  const { product, from } = Route.useSearch();

  const resetStatus = useResetAtom(submissionStatusAtom);
  const resetError = useResetAtom(submissionErrorAtom);

  // The submission atoms are module-global, so a confirm that failed on an earlier
  // visit would otherwise leave this one stuck in "submitting" with a dead button.
  useEffect(() => {
    resetStatus();
    resetError();
  }, [resetStatus, resetError]);

  const productLookupKey = TierLookupKeyEnum[product];
  const priceLookupKey = PRICE_LOOKUP_KEYS[productLookupKey];

  if (!priceLookupKey) {
    throw new Error("priceLookupKey not found");
  }

  /**
   * Return to /checkout/callback?product=[PRODUCT]&from=[FROM_OR_BLANK]
   * /checkout/callback will await the proper AppPermission before redirecting
   * the user to their final destination (`from` or "/").
   */
  const returnUrl = useMemo(() => {
    const url = new URL(window.location.origin + "/checkout/callback");
    url.searchParams.set("product", product);
    if (from?.startsWith("/")) {
      url.searchParams.set("from", from);
    }

    return url.toString();
  }, [from, product]);

  const {
    isPending: isPendingSession,
    data: session,
    error: sessionError,
  } = useQuery(customerQueries.checkoutSession({ returnUrl, priceLookupKey }));

  const handleBack = () => history.back();

  if (isPendingSession) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <p className="text-muted">{t("checkout:loading")}</p>
      </div>
    );
  }

  if (sessionError || !session?.clientSecret) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <p className="text-danger">{t("checkout:generic_error")}</p>
      </div>
    );
  }

  return (
    <div className="px-4 pt-8 lg:px-0">
      <Button
        size="md"
        color="primary-administration"
        variant="text"
        onClick={handleBack}
      >
        <IconArrowBack />
        {t("checkout:back")}
      </Button>

      <div className="flex items-start justify-center pt-12">
        <CheckoutProvider
          stripe={stripePromise}
          options={{ clientSecret: session.clientSecret }}
        >
          <div className="flex w-full flex-col gap-8 pb-8 lg:flex-row lg:gap-16 lg:pb-0 xl:gap-32">
            <OrderSummary recurringAmounts={session.recurringAmounts} />

            <div className="flex flex-col gap-6">
              <PaymentForm />

              <InvoiceContactDialogButton productName={productLookupKey} />
            </div>
          </div>
        </CheckoutProvider>
      </div>
    </div>
  );
}
